import {
  HeyLightOrderDTO,
  HeyLightOrderStatus,
} from "../../HeyLight/heyLightOrderDTO.ts";
import { useMessageController } from "../../UI/useMessageController.ts";
import { useHeyLightOrderBusinessInfoById } from "../../HeyLight/useHeyLightOrderBusinessInfoById.ts";
import { BusinessType } from "../../MoneyTransfer/BusinessType.tsx";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import { Dialog } from "@headlessui/react";
import copy from "copy-to-clipboard";
import dayjs from "dayjs";
import { OrderStatusRenderer } from "./HeyLightOrderStatusRenderer.tsx";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";
import { OutlinedButton } from "@normasteaching/norma-ui";
import { ExpandableCard } from "../../UI/ExpandableCard.tsx";
import { OneOnOneLessonType } from "../../OneOnOneLessonType.ts";
import { useGetHeyLightRefundRecord } from "./useGetHeyLightRefundRecord.ts";
import { formatDateRome } from "../../UI/Utils/formatDateRome.ts";
import { getHeyLightFailureReasonLabel } from "./heyLightFailureReasonLabels.ts";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { CopyableText } from "../../UI/CopyableText.tsx";

const APPROVED_FINANCING_STATUSES = [
  HeyLightOrderStatus.Success,
  HeyLightOrderStatus.Refunded,
  HeyLightOrderStatus.PartiallyRefunded,
];

function describeChosenInstallments(order: HeyLightOrderDTO): string {
  if (order.numberOfInstallments) return `${order.numberOfInstallments} rate`;
  const status = order.heyLightInformation.heyLightOrderStatus;
  if (APPROVED_FINANCING_STATUSES.includes(status))
    return "Non indicate da HeyLight";
  if (status === HeyLightOrderStatus.Cancelled)
    return "Nessuna, finanziamento non approvato";
  return "Non ancora scelte";
}

export function OrderDetailsModal({
  order,
  onClose,
}: {
  order: HeyLightOrderDTO;
  onClose: () => void;
}) {
  const { showMessage, message, open } = useMessageController();
  const { resolveName } = usePurchasedItemNames();

  const { businessInformation } = useHeyLightOrderBusinessInfoById(order._id);

  const isRefunded =
    order.heyLightInformation.heyLightOrderStatus ===
      HeyLightOrderStatus.Refunded ||
    order.heyLightInformation.heyLightOrderStatus ===
      HeyLightOrderStatus.PartiallyRefunded;
  const { refund: refundRecord, loading: loadingRefund } =
    useGetHeyLightRefundRecord(isRefunded ? order._id : null);

  const renderInvoiceInfo = () => {
    if (!order.invoiceInfo.requested) {
      return <p className="text-gray-600">Fattura non richiesta</p>;
    }

    return (
      businessInformation && (
        <div className="space-y-2">
          <div>
            <span className="font-medium">Tipo:</span>{" "}
            {businessInformation.businessType === BusinessType.Person
              ? "Persona Fisica"
              : businessInformation.businessType === BusinessType.Company
                ? "Azienda"
                : "Ditta Individuale"}
          </div>
          <div>
            <span className="font-medium">Codice Fiscale:</span>{" "}
            {businessInformation.taxCode}
          </div>
          {businessInformation.businessType !== BusinessType.Person && (
            <div>
              <span className="font-medium">P.IVA:</span>{" "}
              {businessInformation.vatCode}
            </div>
          )}
          {businessInformation.businessType !== BusinessType.Person &&
            businessInformation.pec && (
              <div>
                <span className="font-medium">PEC:</span>{" "}
                {businessInformation.pec}
              </div>
            )}
          {businessInformation.businessType !== BusinessType.Person &&
            businessInformation.sdiCode && (
              <div>
                <span className="font-medium">Codice SDI:</span>{" "}
                {businessInformation.sdiCode}
              </div>
            )}
          <div>
            <span className="font-medium">Indirizzo:</span>{" "}
            {businessInformation.businessAddress.street},{" "}
            {businessInformation.businessAddress.postalCode}{" "}
            {businessInformation.businessAddress.city} (
            {businessInformation.businessAddress.province})
          </div>
        </div>
      )
    );
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Dettagli Ordine"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-4xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <SuccessMessage open={open} message={message} />
      <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-4xl">
        {/* Informazioni generali */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Informazioni Generali
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Order ID:</span>
              <p
                className="text-gray-600 cursor-pointer hover:text-blue-600"
                onClick={() => {
                  copy(order._id);
                  showMessage({
                    message: "ID dell'ordine copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {order._id}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">HeyLight ID:</span>
              <p
                className="text-gray-600 truncate cursor-pointer hover:text-blue-600"
                title={order.heyLightInformation.heyLightExternalContractUuid}
                onClick={() => {
                  copy(order.heyLightInformation.heyLightExternalContractUuid);
                  showMessage({
                    message: "ID HeyLight copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {order.heyLightInformation.heyLightExternalContractUuid}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Data creazione:</span>
              <p className="text-gray-600">
                {dayjs(order.createdAt).format("DD/MM/YYYY HH:mm:ss")}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Stato:</span>
              <div className="mt-1">
                <OrderStatusRenderer
                  status={order.heyLightInformation.heyLightOrderStatus}
                />
              </div>
            </div>
            {order.heyLightInformation.heyLightOrderStatus ===
              HeyLightOrderStatus.Cancelled && (
              <div>
                <span className="font-medium text-gray-700">
                  Motivo del rifiuto:
                </span>
                <p className="text-gray-600">
                  {getHeyLightFailureReasonLabel(
                    order.heyLightInformation.failureReason,
                  )}
                </p>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">Prezzo:</span>
              <div className="space-y-1">
                {order.coupon ? (
                  <>
                    <p className="text-gray-400 line-through text-sm">
                      €{(order.amountInCent.amount / 100).toFixed(2)}{" "}
                      {order.amountInCent.currency.toUpperCase()}
                    </p>
                    <p className="text-gray-600 ">
                      €
                      {(
                        (order.amountInCent.amount - order.coupon.value) /
                        100
                      ).toFixed(2)}{" "}
                      {order.amountInCent.currency.toUpperCase()}
                    </p>
                  </>
                ) : (
                  <p className="text-gray-600">
                    €{(order.amountInCent.amount / 100).toFixed(2)}{" "}
                    {order.amountInCent.currency.toUpperCase()}
                  </p>
                )}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Rate scelte:</span>
              <p className="text-gray-600">
                {describeChosenInstallments(order)}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">
                {order.productType === NormasTeachingItemType.Bundle
                  ? "Bundle:"
                  : "Prodotto:"}
              </span>
              <p className="text-gray-600 truncate">
                <CopyableText
                  className="cursor-pointer hover:text-blue-600"
                  value={order.productId}
                  title={order.productId}
                  copyMessage={`ID del ${order.productType == NormasTeachingItemType.Bundle ? "bundle" : "prodotto"} copiato negli appunti!`}
                  showMessage={showMessage}
                >
                  {
                    resolveName({
                      id: order.productId,
                      type: order.productType,
                      snapshot: order.productSnapshot,
                    }).name
                  }
                </CopyableText>
              </p>
            </div>
          </div>
        </div>

        {/* Informazioni Cliente */}
        <ExpandableCard title="Informazioni Cliente">
          <div>
            <span className="font-medium text-gray-700">Nome:</span>
            <p className="text-gray-600">
              {order.customerInformation.firstName}{" "}
              {order.customerInformation.lastName}
            </p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Email:</span>
            <p
              className="text-gray-600 cursor-pointer hover:text-blue-600"
              onClick={() => {
                copy(order.customerInformation.email);
                showMessage({
                  message: "Email copiata negli appunti!",
                  timeoutMs: 4000,
                });
              }}
            >
              {order.customerInformation.email}
            </p>
          </div>
        </ExpandableCard>

        {/* Informazioni Fattura */}
        <ExpandableCard title="Informazioni Fattura">
          {renderInvoiceInfo()}
        </ExpandableCard>

        {/* Informazioni Coupon/Sconto */}
        {order.coupon && (
          <ExpandableCard title="Coupon Applicato">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Codice:</span>
              <code className="bg-white px-2 py-1 rounded border border-gray-300 text-gray-700 font-mono">
                {order.coupon.couponCode}
              </code>
            </div>
            <div>
              <span className="font-medium text-gray-900">ID Coupon:</span>
              <span className="text-gray-700 ml-2 text-sm font-mono">
                {order.coupon._id}
              </span>
            </div>
          </ExpandableCard>
        )}

        {/* Informazioni One on One */}
        {order.productSnapshot?.fpsProductInfo?.oneOnOne && (
          <ExpandableCard title="Informazioni One on One">
            <div>
              <span className="font-medium text-gray-700">Totale lezioni:</span>
              <p className="text-gray-600">
                {order.productSnapshot.fpsProductInfo.oneOnOneTotalLessons ?? 0}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">
                Tipo di lezione:
              </span>
              <p className="text-gray-600">
                {order.productSnapshot.fpsProductInfo.oneOnOneLessonType ===
                OneOnOneLessonType.GeneralEnglish
                  ? "General English"
                  : order.productSnapshot.fpsProductInfo.oneOnOneLessonType ===
                      OneOnOneLessonType.BusinessEnglish
                    ? "Business English"
                    : "N/A"}
              </p>
            </div>
          </ExpandableCard>
        )}

        {/* Partecipanti Bundle */}
        {order.bundleParticipants && order.bundleParticipants.length > 0 && (
          <ExpandableCard title="Partecipanti Bundle">
            {order.bundleParticipants.map((participant, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-md space-y-2">
                <div className="font-medium text-gray-900">
                  Partecipante {index + 1}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Nome:</span>{" "}
                  {participant.customer.firstName}{" "}
                  {participant.customer.lastName}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Email:</span>{" "}
                  <span
                    className="text-gray-600 cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      copy(participant.customer.email);
                      showMessage({
                        message: "Email copiata negli appunti!",
                        timeoutMs: 4000,
                      });
                    }}
                  >
                    {participant.customer.email}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-sm text-gray-700">Prodotti:</span>
                  {participant.products.map((product, pIndex) => (
                    <div key={pIndex} className="text-sm text-gray-600 ml-3">
                      •{" "}
                      <CopyableText
                        className="cursor-pointer hover:text-blue-600"
                        value={product.id}
                        title={product.id}
                        copyMessage="ID del prodotto copiato negli appunti!"
                        showMessage={showMessage}
                      >
                        {
                          resolveName({
                            id: product.id,
                            type: NormasTeachingItemType.Product,
                          }).name
                        }
                      </CopyableText>{" "}
                      - €{(product.priceInCents.amount / 100).toFixed(2)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </ExpandableCard>
        )}

        {/* Altri Partecipanti */}
        {order.participants && order.participants.length > 0 && (
          <ExpandableCard title="Altri Partecipanti">
            {order.participants.map((participant, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-md space-y-1">
                <div>
                  <span className="text-sm text-gray-700">Nome:</span>{" "}
                  {participant.firstName} {participant.lastName}
                </div>
                <div>
                  <span className="text-sm text-gray-700">Email:</span>{" "}
                  <span
                    className="text-gray-600 cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      copy(participant.email);
                      showMessage({
                        message: "Email copiata negli appunti!",
                        timeoutMs: 4000,
                      });
                    }}
                  >
                    {participant.email}
                  </span>
                </div>
              </div>
            ))}
          </ExpandableCard>
        )}

        {/* Rimborso */}
        {isRefunded && (
          <ExpandableCard title="Rimborso">
            {loadingRefund ? (
              <p className="text-gray-500 text-sm">Caricamento...</p>
            ) : refundRecord ? (
              <>
                {refundRecord.amountInCent && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Importo rimborsato:
                    </span>
                    <p className="text-gray-600 font-medium">
                      €{(refundRecord.amountInCent.amount / 100).toFixed(2)}{" "}
                      {refundRecord.amountInCent.currency.toUpperCase()}
                    </p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Tipo:</span>
                  <p className="text-gray-600">
                    {refundRecord.totalRefund === false
                      ? "Rimborso parziale"
                      : "Rimborso totale"}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Data:</span>
                  <p className="text-gray-600">
                    {formatDateRome(refundRecord.createdAt)}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-sm">
                Nessun record di rimborso trovato.
              </p>
            )}
          </ExpandableCard>
        )}

        {/* Pulsante chiudi */}
        <div className="pt-4 border-t">
          <OutlinedButton onClick={onClose} label="Chiudi" />
        </div>
      </div>
    </Modal>
  );
}
